<template>
  <div class="reports-incomes-page">
    <ReportsPageShell
      :title="currentHeaderTitle"
      :subtitle="currentHeaderSubtitle"
      :actions="headerActions"
      @action="onHeaderAction"
    >
      <template #income-tabs>
        <ReportsPrimaryTabs
          :tabs="reportTabs"
          :active-tab="activeReportTab"
          @select="handleReportTabSelect"
        /> 
      </template>

      <template #metrics>
        <ReportsKpiSection
          :cards="kpiCards"
          :desktop-columns="4"
          :tablet-columns="2"
        />
      </template>

      <template #toolbar>
        <ReportsFilterToolbar
          report-key="incomes"
          :report-tab="activeReportTab"
          :selectors-fetcher="fetchIncomesSelectors"
          @filters-change="handleToolbarFiltersChange"
          @apply="handleApplyFilters"
        />
      </template>

      <template #chart>
        <ReportsChartSection
          :mode="chartMode"
          :points="chartPoints"
          :allow-visibility-toggle="true"
          visibility-storage-key="reports.incomes.chart.hidden"
          @update:mode="chartMode = $event"
        />
      </template>

      <template #table>
        <ReportsTable
          :table="tableData"
          sticky-header
          :allow-totals-detail="true"
          :sort-direction="activeSortDirection"
          @period-cell-click="handlePeriodCellClick"
        />
      </template>
    </ReportsPageShell>

    <ReportsDetailModal
      :show="detailModal.show"
      :loading="detailModal.loading"
      :title="detailModal.title"
      :context-line="detailModal.contextLine"
      :subtitle="detailModal.subtitle"
      :columns="detailModal.columns"
      :rows="detailModal.rows"
      :grand-total="detailModal.grandTotal"
      @close="closeDetailModal"
    />
  </div>
</template>

<script>
import ReportsPageShell from '@/components/reports/ReportsPageShell.vue';
import ReportsPrimaryTabs from '@/components/reports/ReportsPrimaryTabs.vue';
import ReportsKpiSection from '@/components/reports/ReportsKpiSection.vue';
import ReportsFilterToolbar from '@/components/reports/ReportsFilterToolbar.vue';
import ReportsChartSection from '@/components/reports/ReportsChartSection.vue';
import ReportsTable from '@/components/reports/ReportsTable.vue';
import ReportsDetailModal from '@/components/reports/ReportsDetailModal.vue';
import reportsIncomesReceivablesService from '@/services/reportsIncomesReceivables';
import { extractPayload } from '@/utils/dataNormalization';
import { mapReportsChartPoints } from '@/composables/reports/general/useReportsPresentation';
import { downloadBlobFile } from '@/utils/fileDownload';
import {
  REPORTS_SALES_TEXT,
  requireReportsSalesTextValue,
  normalizeReportsTablePayload,
} from '@/composables/reports/sales/useReportsSalesPresentation';
import {
  REPORTS_SORT_DIRECTION_NONE,
  hasReportsToolbarNonSortChanges,
  normalizeReportsToolbarFiltersPayload,
  resolveReportsToolbarSortChange,
} from '@/composables/reports/general/toolbar/useReportsFilterToolbarHelpers';
import {
  REPORTS_INCOMES_RECEIVABLES_FALLBACK_REPORT_CODE,
  REPORTS_INCOMES_RECEIVABLES_EXPORTABLE_CODES,
  normalizeReportsIncomesReceivablesReportCode,
  normalizeReportsIncomesReceivablesTabs,
  normalizeReportsIncomesReceivablesMetricCards,
  buildReportsIncomesReceivablesHeaderActions,
  buildReportsIncomesReceivablesExportFileName,
  buildReportsIncomesReceivablesFallbackTab,
  buildReportsIncomesReceivablesHeaderSubtitle,
} from '@/composables/reports/incomes/useReportsIncomesReceivablesPresentation';

const INCOMES_FALLBACK_TAB = REPORTS_INCOMES_RECEIVABLES_FALLBACK_REPORT_CODE;
const INCOMES_FALLBACK_TABS = [buildReportsIncomesReceivablesFallbackTab()];

export default {
  name: 'ReportsIncomesReceivablesPage',
  components: {
    ReportsPageShell,
    ReportsPrimaryTabs,
    ReportsKpiSection,
    ReportsFilterToolbar,
    ReportsChartSection,
    ReportsTable,
    ReportsDetailModal,
  },
  data() {
    return {
      chartMode: 'bar',
      activeReportTab: INCOMES_FALLBACK_TAB,
      reportTabs: [...INCOMES_FALLBACK_TABS],
      headerActions: buildReportsIncomesReceivablesHeaderActions(),
      lastAppliedFilters: null,
      rawMetricsCards: [],
      chartPointsData: [],
      tableData: {
        headers: [],
        rows: [],
        period_totals: [],
        grand_total: 0,
      },
      detailModal: {
        show: false,
        loading: false,
        title: '',
        contextLine: '',
        subtitle: '',
        columns: [],
        rows: [],
        grandTotal: 0,
      },
      activeSortDirection: REPORTS_SORT_DIRECTION_NONE,
    };
  },
  async mounted() {
    await this.loadReportTabs();
    await this.refreshReportData();
  },
  computed: {
    currentTabConfig() {
      const match = this.reportTabs.find((tab) => tab.key === this.activeReportTab);
      return match || this.reportTabs[0] || null;
    },
    currentHeaderTitle() {
      return this.currentTabConfig?.headerTitle || 'Reportes de cobranza';
    },
    currentHeaderSubtitle() {
      const tabLabel = String(this.currentTabConfig?.label || '').trim();
      return buildReportsIncomesReceivablesHeaderSubtitle(tabLabel);
    },
    kpiCards() {
      return normalizeReportsIncomesReceivablesMetricCards(this.rawMetricsCards);
    },
    chartPoints() {
      return this.chartPointsData;
    },
  },
  methods: {
    async loadReportTabs() {
      const localTabs = [...INCOMES_FALLBACK_TABS];

      try {
        const response = await reportsIncomesReceivablesService.selectors(
          { report: normalizeReportsIncomesReceivablesReportCode(this.activeReportTab) },
          { skipLoading: true }
        );
        const payload = extractPayload(response);
        const backendTabs = normalizeReportsIncomesReceivablesTabs(payload?.reports || payload?.tabs);
        const allTabs = backendTabs.length > 0 ? backendTabs : localTabs;

        this.reportTabs = allTabs;

        const defaultTab = allTabs.find((tab) => tab?.isDefault);
        if (defaultTab) {
          this.activeReportTab = this.normalizeIncomesReportCode(defaultTab.key);
          return;
        }

        const isActiveTabValid = allTabs.some((tab) => tab.key === this.activeReportTab);
        if (!isActiveTabValid) {
          this.activeReportTab = String(allTabs[0]?.key || INCOMES_FALLBACK_TAB);
        }
      } catch (error) {
        this.reportTabs = localTabs;
        this.activeReportTab = INCOMES_FALLBACK_TAB;
      }
    },
    normalizeIncomesReportCode(value) {
      return normalizeReportsIncomesReceivablesReportCode(value);
    },
    extractSingleFilterValue(source) {
      if (Array.isArray(source) && source.length > 0) {
        return String(source[0] || '').trim();
      }

      return String(source || '').trim();
    },
    buildRequestParams() {
      const filters = this.lastAppliedFilters && typeof this.lastAppliedFilters === 'object'
        ? { ...this.lastAppliedFilters }
        : {};
      delete filters.sort_direction;

      const displayCurrency = this.extractSingleFilterValue(filters.display_currency);
      const invoiced = this.extractSingleFilterValue(filters.invoiced);
      const receiptType = this.extractSingleFilterValue(filters.receipt_type);
      const exchangeRate = Number(filters.exchange_rate || 0);

      if (displayCurrency !== '') {
        filters.display_currency = displayCurrency;
      }

      if (displayCurrency.toLowerCase() !== 'mxn' && Number.isFinite(exchangeRate) && exchangeRate > 0) {
        filters.exchange_rate = exchangeRate;
      } else {
        delete filters.exchange_rate;
      }

      if (invoiced !== '') {
        filters.invoiced = invoiced;
      }

      if (receiptType !== '') {
        filters.receipt_type = receiptType;
      }

      return {
        ...filters,
        report: this.normalizeIncomesReportCode(this.activeReportTab),
      };
    },
    handleToolbarFiltersChange(payload, meta = {}) {
      const { sortDirection, nextFilters } = resolveReportsToolbarSortChange({
        payload,
        meta,
        currentFilters: this.lastAppliedFilters,
      });

      this.activeSortDirection = sortDirection;
      this.lastAppliedFilters = nextFilters;
    },
    async fetchIncomesSelectors(params, config) {
      const requestedReport = this.normalizeIncomesReportCode(
        params?.report_tab || params?.report || this.activeReportTab
      );

      return reportsIncomesReceivablesService.selectors(
        {
          ...params,
          report: requestedReport,
        },
        config
      );
    },
    async fetchReportData({ skipLoading = false } = {}) {
      const requestConfig = skipLoading ? { skipLoading: true } : {};
      const response = await reportsIncomesReceivablesService.table(this.buildRequestParams(), requestConfig);
      const payload = extractPayload(response);

      this.rawMetricsCards = Array.isArray(payload?.cards) ? payload.cards : [];
      this.tableData = normalizeReportsTablePayload(payload?.table || {});
      this.chartPointsData = mapReportsChartPoints(payload?.chart || {});
    },
    async refreshReportData({ skipLoading = false } = {}) {
      try {
        await this.fetchReportData({ skipLoading });
      } catch (error) {
        this.rawMetricsCards = [];
        this.tableData = normalizeReportsTablePayload({});
        this.chartPointsData = [];
      }
    },
    async handleReportTabSelect(tabKey) {
      const nextTab = this.normalizeIncomesReportCode(tabKey);
      if (nextTab === '' || nextTab === this.activeReportTab) {
        return;
      }

      this.closeDetailModal();
      this.activeReportTab = nextTab;
      await this.refreshReportData();
    },
    buildExportFileName(extension) {
      const reportCode = normalizeReportsIncomesReceivablesReportCode(this.activeReportTab);
      return buildReportsIncomesReceivablesExportFileName(reportCode, extension);
    },
    async onHeaderAction(action) {
      const actionKey = String(action?.key || '').trim().toLowerCase();
      if (!['excel', 'pdf'].includes(actionKey)) {
        return;
      }

      const reportCode = normalizeReportsIncomesReceivablesReportCode(this.activeReportTab);
      if (!REPORTS_INCOMES_RECEIVABLES_EXPORTABLE_CODES.includes(reportCode)) {
        this.$toast?.info?.(
          requireReportsSalesTextValue(
            REPORTS_SALES_TEXT.page.toasts.exportUnavailable,
            'reports.sales.page.toasts.exportUnavailable'
          )
        );
        return;
      }

      try {
        const params = this.buildRequestParams();
        const response = actionKey === 'excel'
          ? await reportsIncomesReceivablesService.excel(params)
          : await reportsIncomesReceivablesService.pdf(params);
        const extension = actionKey === 'excel' ? 'xlsx' : 'pdf';
        const mimeType = actionKey === 'excel'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/pdf';

        downloadBlobFile(response, this.buildExportFileName(extension), mimeType);

        this.$toast?.success(
          actionKey === 'excel'
            ? requireReportsSalesTextValue(REPORTS_SALES_TEXT.page.toasts.excelSuccess, 'reports.sales.page.toasts.excelSuccess')
            : requireReportsSalesTextValue(REPORTS_SALES_TEXT.page.toasts.pdfSuccess, 'reports.sales.page.toasts.pdfSuccess')
        );
      } catch (error) {
        this.$toast?.error(
          error?.response?.data?.message
            || error?.message
            || (actionKey === 'excel'
              ? requireReportsSalesTextValue(REPORTS_SALES_TEXT.page.toasts.excelError, 'reports.sales.page.toasts.excelError')
              : requireReportsSalesTextValue(REPORTS_SALES_TEXT.page.toasts.pdfError, 'reports.sales.page.toasts.pdfError'))
        );
      }
    },
    async handleApplyFilters(payload) {
      this.closeDetailModal();

      const nextPayload = normalizeReportsToolbarFiltersPayload(payload);
      const shouldReloadData = hasReportsToolbarNonSortChanges(nextPayload, this.lastAppliedFilters);

      this.lastAppliedFilters = nextPayload;
      this.activeSortDirection = nextPayload.sort_direction;

      if (shouldReloadData) {
        await this.refreshReportData({ skipLoading: false });
      }
    },
    closeDetailModal() {
      this.detailModal = {
        ...this.detailModal,
        show: false,
      };
    },
    buildDetailRequestParams(payload = {}) {
      const requestParams = {
        ...this.buildRequestParams(),
      };

      if (Array.isArray(payload.element_ids) && payload.element_ids.length > 0) {
        requestParams.element_ids = payload.element_ids;
      }

      const detailSource = String(payload.detail_source || '').trim().toLowerCase();
      if (detailSource !== '') {
        requestParams.detail_source = detailSource;
      }

      const detailMetric = String(payload.detail_metric || '').trim().toLowerCase();
      if (detailMetric !== '') {
        requestParams.detail_metric = detailMetric;
      }

      return requestParams;
    },
    buildDetailContextLine(payload = {}) {
      const primaryPivotName = String(payload?.primary_pivot_name || '').trim();
      const secondaryPivotName = String(payload?.secondary_pivot_name || '').trim();
      const columnLabel = String(payload?.column_label || payload?.metric_label || '').trim();

      return [primaryPivotName, secondaryPivotName, columnLabel]
        .filter((value) => value !== '')
        .join(' - ');
    },
    async handlePeriodCellClick(payload) {
      if (!Array.isArray(payload?.element_ids) || payload.element_ids.length === 0) {
        return;
      }

      const metricLabel = String(payload?.metric_label || '').trim();
      const reportLabel = this.currentTabConfig?.label
        || requireReportsSalesTextValue(REPORTS_SALES_TEXT.detail.defaultReportLabel, 'reports.sales.detail.defaultReportLabel');
      const detailTitlePrefix = requireReportsSalesTextValue(REPORTS_SALES_TEXT.detail.titlePrefix, 'reports.sales.detail.titlePrefix');
      const detailTitle = metricLabel !== ''
        ? `${detailTitlePrefix} ${metricLabel}`
        : `${detailTitlePrefix} ${reportLabel}`;
      const detailContextLine = this.buildDetailContextLine(payload);

      this.detailModal = {
        ...this.detailModal,
        show: true,
        loading: true,
        title: detailTitle,
        contextLine: detailContextLine,
        subtitle: requireReportsSalesTextValue(REPORTS_SALES_TEXT.detail.loadingSubtitle, 'reports.sales.detail.loadingSubtitle'),
        columns: [],
        rows: [],
        grandTotal: 0,
      };

      try {
        const response = await reportsIncomesReceivablesService.detail(
          this.buildDetailRequestParams(payload),
          { skipLoading: true }
        );
        const data = extractPayload(response);
        const detail = data?.detail || {};
        const columns = Array.isArray(detail?.columns) ? detail.columns : [];
        const rows = Array.isArray(detail?.rows) ? detail.rows : [];
        const count = Number(detail?.count || rows.length || 0);

        this.detailModal = {
          ...this.detailModal,
          loading: false,
          title: detailTitle,
          contextLine: detailContextLine,
          subtitle: `${count} ${count === 1
            ? requireReportsSalesTextValue(REPORTS_SALES_TEXT.detail.recordLabelSingular, 'reports.sales.detail.recordLabelSingular')
            : requireReportsSalesTextValue(REPORTS_SALES_TEXT.detail.recordLabelPlural, 'reports.sales.detail.recordLabelPlural')}`,
          columns,
          rows,
          grandTotal: Number(detail?.grand_total ?? 0),
        };
      } catch (error) {
        this.detailModal = {
          ...this.detailModal,
          loading: false,
          contextLine: detailContextLine,
          subtitle: requireReportsSalesTextValue(REPORTS_SALES_TEXT.detail.loadErrorSubtitle, 'reports.sales.detail.loadErrorSubtitle'),
          columns: [],
          rows: [],
          grandTotal: 0,
        };
      }
    },
  },
};
</script>
